import { fetcher } from '@data/utils/api/fetcher/fetcher';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { z } from 'zod';

/** Only the name parts and the id are used; the rest of the record is ignored. */
const teacherShape = z.object({
  user_id: z.string(),
  given_name: z.string().nullish(),
  family_name: z.string().nullish(),
  middle_name: z.string().nullish(),
});

const teachersPageShape = z.object({
  content: z.array(teacherShape).nullish(),
});

export interface Teacher {
  userId: string;
  firstName: string;
  lastName: string;
  middleName?: string;
}

/** Fixed for this call; only the organization varies. */
const TEACHER_QUERY_DEFAULTS = {
  userRoles: 'teacher',
  includeChildren: 'false',
  membershipTier: 'all',
  page: '0',
  size: '3000',
} as const;

export async function getTeachersByOrgApi(orgId: string): Promise<Teacher[]> {
  const response = await fetcher(`/composite/users/orgs/${orgId}`, {
    service: '/roster',
    version: '/v3',
    queryParameters: { ...TEACHER_QUERY_DEFAULTS },
  });

  const { content } = teachersPageShape.parse(response);

  return (content ?? []).map((teacher) => ({
    userId: teacher.user_id,
    firstName: teacher.given_name ?? '',
    lastName: teacher.family_name ?? '',
    ...(teacher.middle_name ? { middleName: teacher.middle_name } : {}),
  }));
}

export const getTeachersByOrgApiQueryKey = (orgId: string) =>
  ['teachers-by-org', orgId] as const;

/** Query options for use with `queryClient.ensureQueryData` in route loaders. */
export function teachersByOrgQuery(orgId: string) {
  return {
    queryKey: getTeachersByOrgApiQueryKey(orgId),
    queryFn: () => getTeachersByOrgApi(orgId),
  };
}

/** The teachers in one organization, for the admin organization report filter. */
export function useGetTeachersByOrgApi(
  orgId: string,
  customQueryOptions?: Partial<UseQueryOptions<Teacher[]>>
) {
  const { enabled = true } = customQueryOptions ?? {};

  return useQuery<Teacher[]>({
    ...teachersByOrgQuery(orgId),
    staleTime: 3 * 60 * 1000,
    ...customQueryOptions,
    // Last, so a caller's own `enabled` narrows this guard rather than
    // replacing it: a request with no ids is never worth sending.
    enabled: enabled && Boolean(orgId),
  });
}
